"use client";

import { Divider, chakra, useMultiStyleConfig } from "@chakra-ui/react";
import { useParams } from "next/navigation";
import React from "react";
import { NextLink } from "src/components/NextLink";
import {
	isStructuredText,
	StructuredText,
} from "src/components/StructuredText/StructuredText";
import { useTranslatedString } from "src/i18n/hooks";
import { Facebook, LinkedIn } from "src/icons";
import { BlogpostPageProps } from "src/lib/cms/blogpost";
import { getBaseUrl } from "src/lib/deployments";
import { isString, phoneNumberDisplayToTel, trimSlashes } from "src/lib/utils";
import { ContactPhoto } from "../Contact/Contact";
import { DateDisplay } from "../DateDisplay/DateDisplay";
import { HeadingLevelBoundary, Hx } from "../Heading/Heading";

type Service = "LinkedIn" | "Facebook";

export type BlogpostProps = {
	blogpost: NonNullable<BlogpostPageProps>["blogpost"];
};

const SocialLink: React.FC<{
	service: Service;
	title: string;
}> = React.memo(({ service, title }) => {
	const t = useTranslatedString();
	const params = useParams();
	// This is null in a story context
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	const brandSlug = params?.brandSlug;
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	const slug = params?.slug;

	if (!isString(brandSlug) || !isString(slug)) {
		return null;
	}

	const baseUrl = getBaseUrl(brandSlug);

	const canonicalHref = new URL(
		[t("blog.urlSlug"), slug].map(trimSlashes).join("/"),
		baseUrl,
	).toString();

	const encodedBlogpostTitle = encodeURIComponent(title);
	const encodedBlogpostUrl = encodeURIComponent(canonicalHref);

	let shareLink;
	let SocialIcon;

	switch (service) {
		case "LinkedIn":
			SocialIcon = LinkedIn;
			shareLink = `https://www.linkedin.com/shareArticle?url=${encodedBlogpostUrl}&title=${encodedBlogpostTitle}`;
			break;
		case "Facebook":
			SocialIcon = Facebook;
			shareLink = `https://www.facebook.com/sharer.php?u=${encodedBlogpostUrl}`;
			break;
		default:
			return null;
	}

	return (
		<chakra.a
			target="_blank"
			rel="noopener noreferrer"
			href={shareLink}
			aria-label={service}
		>
			<SocialIcon boxSize={{ base: 8, lg: 6 }} />
		</chakra.a>
	);
});

SocialLink.displayName = "SocialLink";

const ShareBlogpost: React.FC<{ title: string }> = ({ title }) => {
	const t = useTranslatedString();
	const styles = useMultiStyleConfig("Blogpost", { breakpoint: "md" });

	return (
		<chakra.div display="flex" flexDir="column" gap={2}>
			<chakra.strong fontSize="sm">{t("blogpost.share")}</chakra.strong>
			<chakra.div __css={styles.socialLinks}>
				<SocialLink service="LinkedIn" title={title} />
				<SocialLink service="Facebook" title={title} />
			</chakra.div>
		</chakra.div>
	);
};

const Author: React.FC<{
	author: NonNullable<BlogpostPageProps>["blogpost"]["author"];
}> = ({ author }) => {
	const t = useTranslatedString();
	const styles = useMultiStyleConfig("Blogpost");
	const { name, description, phoneNumber, image } = author;

	return (
		<chakra.div display="flex" flexDir="row" gap={4}>
			<ContactPhoto name={name} photo={image} sx={styles.authorImage} />
			<chakra.div display="flex" flexDir="column" fontSize="0.75rem">
				<chakra.strong flexGrow={1}>
					{t("blogpost.author", { name })}
				</chakra.strong>
				{description}
				<a href={`tel:${phoneNumberDisplayToTel(phoneNumber)}`}>
					{phoneNumber}
				</a>
			</chakra.div>
		</chakra.div>
	);
};

export const Blogpost: React.FC<BlogpostProps> = ({ blogpost }) => {
	const t = useTranslatedString();
	const { title, text, readingTime, date, productFamilies, author } = blogpost;

	const topicLinks = productFamilies.map((family) =>
		<NextLink
			key={family.slug}
			href={`${t("blog.urlSlug")}?topic=${
				family.slug
			}`}
			passHref
		>
			<chakra.a
				id={family.slug}
				className="font-semibold py-2 px-4 rounded-xl text-white shadow-lg w-fit"
				style={{
					backgroundColor:
						family.color.hex,
				}}
			>
				{family.name}
			</chakra.a>
		</NextLink>
	);

	return (
		<HeadingLevelBoundary>
			<div className="grid grid-cols-[1fr_min(1280px,100%)_1fr] text-brand-blue py-12 px-4">
				<div className="col-start-2 inline-grid md:grid-cols-[min-content_auto] md:gap-x-16 grid-cols-1">
					<aside className="font-semibold whitespace-nowrap mx-auto col-start-1 text-sm pb-12 md:pb-0">
						<chakra.span>
							{t("blogpost.date", {
								date: <DateDisplay date={date} />,
							})}
						</chakra.span>
						<Divider marginTop={2} marginBottom={2} />
						<chakra.span>
							{t("blogpost.readingTime", {
								readingTime,
								count: readingTime !== 1 && "n",
							})}
						</chakra.span>
					</aside>
					<article className="contents md:*:col-start-2">
						<Hx
							className="pb-8 text-center md:text-left text-4xl!"
							size="h2"
						>
							{title}
						</Hx>
						<div className="contents [&>h2]:text-2xl [&>h3]:text-xl md:*:col-start-2">
							{isStructuredText(text) && (
								<StructuredText data={text} />
							)}
						</div>
						<div className="flex flex-col items-center md:items-start pt-8">
							<div className="flex flex-col items-start">
								<chakra.span fontWeight={600} marginBottom={4}>
									{t("blogpost.topic")}
								</chakra.span>
								<div className="flex gap-3">{topicLinks}</div>
							</div>
						</div>
						<Divider marginTop={12} marginBottom={12} />
						<footer className="flex flex-col md:flex-row items-center justify-between gap-6">
							<Author author={author} />
							<ShareBlogpost title={blogpost.title} />
						</footer>
					</article>
				</div>
			</div>
		</HeadingLevelBoundary>
	);
};

// 🔬 e2e: story
